import { minutes, money, type AssignmentId, type ISODateTime, type Minutes, type Money } from '../domain/branded.js';
import type { Assignment } from '../domain/assignment.js';
import type { Project } from '../domain/project.js';
import type { Resource } from '../domain/resource.js';
import type { Timesheet, TimesheetStatus } from '../domain/timesheet.js';
import { InvalidArgumentError } from '../domain/errors.js';
import { resolveResourceRate } from './rate-tables.js';

/**
 * 工時表狀態流轉：Draft -> Submitted -> Approved | Rejected；
 * Submitted -> Recalled（收回）；Rejected -> Draft（重新編輯後可再送出，由呼叫端另行處理）。
 */
const ALLOWED_TRANSITIONS: Readonly<Record<TimesheetStatus, readonly TimesheetStatus[]>> = {
  Draft: ['Submitted'],
  Submitted: ['Approved', 'Rejected', 'Recalled'],
  Approved: [],
  Rejected: ['Draft'],
  Recalled: ['Draft'],
};

function transition(timesheet: Timesheet, to: TimesheetStatus): void {
  const allowed = ALLOWED_TRANSITIONS[timesheet.status];
  if (!allowed.includes(to)) {
    throw new InvalidArgumentError(
      `Timesheet ${timesheet.id as string}: cannot transition from ${timesheet.status} to ${to}`,
    );
  }
}

export function submitTimesheet(timesheet: Timesheet, submittedAt: ISODateTime): Timesheet {
  transition(timesheet, 'Submitted');
  return { ...timesheet, status: 'Submitted', submittedAt };
}

export function approveTimesheet(
  timesheet: Timesheet,
  approvedBy: Timesheet['approvedBy'],
  approvedAt: ISODateTime,
): Timesheet {
  transition(timesheet, 'Approved');
  return { ...timesheet, status: 'Approved', approvedAt, approvedBy, rejectionReason: null };
}

export function rejectTimesheet(timesheet: Timesheet, reason: string): Timesheet {
  transition(timesheet, 'Rejected');
  return { ...timesheet, status: 'Rejected', rejectionReason: reason };
}

export function recallTimesheet(timesheet: Timesheet): Timesheet {
  transition(timesheet, 'Recalled');
  return { ...timesheet, status: 'Recalled' };
}

/** 單一指派的工時彙總（來自已核准工時表的逐日紀錄）。 */
export interface TimesheetAggregate {
  readonly assignmentId: AssignmentId;
  readonly actualWork: Minutes;
  readonly overtimeWork: Minutes;
  readonly remainingWork: Minutes | null;
}

/** 彙總一批「已核准」工時表中，逐指派的實際工時（未核准者一律忽略）。 */
export function aggregateApprovedTimesheets(
  timesheets: readonly Timesheet[],
): ReadonlyMap<AssignmentId, TimesheetAggregate> {
  const byAssignment = new Map<AssignmentId, { actualWork: number; overtimeWork: number; remainingWork: number | null }>();

  for (const timesheet of timesheets) {
    if (timesheet.status !== 'Approved') continue;
    for (const entry of timesheet.entries) {
      if (entry.assignmentId === null) continue;
      const current = byAssignment.get(entry.assignmentId) ?? { actualWork: 0, overtimeWork: 0, remainingWork: null };
      current.actualWork += entry.actualWork as number;
      current.overtimeWork += entry.overtimeWork as number;
      if (entry.remainingWork !== null) {
        current.remainingWork = entry.remainingWork as number;
      }
      byAssignment.set(entry.assignmentId, current);
    }
  }

  const result = new Map<AssignmentId, TimesheetAggregate>();
  for (const [assignmentId, agg] of byAssignment) {
    result.set(assignmentId, {
      assignmentId,
      actualWork: minutes(agg.actualWork),
      overtimeWork: minutes(agg.overtimeWork),
      remainingWork: agg.remainingWork === null ? null : minutes(agg.remainingWork),
    });
  }
  return result;
}

/**
 * 把已核准工時表回填到專案的指派上：更新 `actualWork` / `remainingWork` / `overtimeWork`，
 * 並依資源費率重算 `actualCost`。回傳新的 Project（不修改輸入）。
 */
export function applyApprovedTimesheets(project: Project, timesheets: readonly Timesheet[]): Project {
  const aggregates = aggregateApprovedTimesheets(timesheets);
  if (aggregates.size === 0) return project;

  const resourcesById = new Map<string, Resource>(project.resources.map((r) => [r.id as string, r]));

  const assignments: Assignment[] = project.assignments.map((assignment) => {
    const agg = aggregates.get(assignment.id);
    if (agg === undefined) return assignment;

    const resource = resourcesById.get(assignment.resourceId as string);
    const actualWork = agg.actualWork;
    const overtimeWork = agg.overtimeWork;
    let actualCostValue = 0;
    if (resource) {
      // Timesheet entries retain their work date, so approved actuals use the
      // effective table rate for that day rather than today's resource rate.
      for (const sheet of timesheets) {
        if (sheet.status !== 'Approved' || sheet.resourceId !== assignment.resourceId) continue;
        for (const entry of sheet.entries) {
          if (entry.assignmentId !== assignment.id) continue;
          const rate = resolveResourceRate(resource, assignment.rateTable, `${entry.date as string}T00:00:00` as ISODateTime);
          const overtime = Math.min(entry.actualWork as number, entry.overtimeWork as number);
          actualCostValue += ((entry.actualWork as number) - overtime) * (rate.standardRate as number) + overtime * (rate.overtimeRate as number);
        }
      }
    }
    const actualCost: Money = money(actualCostValue);

    const remainingWork = agg.remainingWork ?? minutes(Math.max(0, (assignment.work as number) - (actualWork as number)));

    return {
      ...assignment,
      actualWork,
      overtimeWork,
      remainingWork,
      actualCost,
    };
  });

  return { ...project, assignments };
}
